import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const PRODUCTS = [
  {
    title: "Stand-up Pouches",
    desc: "Self-standing pouches with bottom gusset. The shelf-ready hero of D2C food brands.",
    img: "https://customer-assets.emergentagent.com/job_design-your-site-12/artifacts/nqz9km26_standup.png",
    span: "lg:col-span-7 lg:row-span-2",
  },
  {
    title: "Spouted Pouches",
    desc: "For juices, sauces and liquids. Resealable cap, single-hand pour.",
    img: "https://customer-assets.emergentagent.com/job_design-your-site-12/artifacts/wgfam38c_spout.png",
    span: "lg:col-span-5",
  },
  {
    title: "Center-seal Bags",
    desc: "Coffee, snacks, protein. Premium matte finish with degassing valve options.",
    img: "https://customer-assets.emergentagent.com/job_design-your-site-12/artifacts/ltfexa94_cs.png",
    span: "lg:col-span-5",
  },
  {
    title: "3-side Seal Sachets",
    desc: "Single-serve sachets for samples, sauces, beauty trials and pharma.",
    img: "https://customer-assets.emergentagent.com/job_design-your-site-12/artifacts/v5r8le8d_3ss.png",
    span: "lg:col-span-4",
  },
  {
    title: "Flat Bottom Pouches",
    desc: "Premium box-shaped pouches with 5 panels of full-color print area.",
    img: "/pouch-flat-bottom.png",
    span: "lg:col-span-4",
  },
  {
    title: "Custom Die-Cut",
    desc: "Bottle-shaped, animal-shaped, anything-shaped. Your imagination, our die.",
    img: "https://customer-assets.emergentagent.com/job_design-your-site-12/artifacts/nuymodu3_shape.png",
    span: "lg:col-span-4",
  },
];

export default function ProductGallery() {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="bg-[#F8F9FA] py-20 md:py-28 border-y border-[#d1d5db]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#1B3F8F] mb-4 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#F58220]" />
              Our catalogue
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black uppercase leading-[0.95] tracking-tighter max-w-3xl">
              Pouches engineered<br />for shelf, for hand, for love.
            </h2>
          </div>
          <a
            href="#quote"
            data-testid="products-cta-quote"
            className="inline-flex items-center gap-2 self-start md:self-end border-b-2 border-[#F58220] text-[#1B3F8F] hover:text-[#F58220] transition-colors py-2 text-[13px] font-bold uppercase tracking-[0.16em]"
          >
            Spec a custom pouch
            <ArrowUpRight size={16} />
          </a>
        </div>

        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-4"
          data-testid="products-bento"
        >
          {PRODUCTS.map((p, i) => (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`product-card-${i}`}
              className={`relative overflow-hidden border border-[#d1d5db] group min-h-[280px] ${p.span}`}
              style={
                p.solid ? { backgroundColor: p.color, color: "white" } : {}
              }
            >
              {p.img && (
                <>
                  <img
                    src={p.img}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1128]/85 via-[#0a1128]/30 to-transparent" />
                </>
              )}

              <div
                className={`relative z-10 h-full min-h-[280px] flex flex-col justify-between p-6 md:p-8 ${
                  p.img ? "text-white" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-[0.24em] ${
                      p.img ? "text-white/80" : "text-white/80"
                    }`}
                  >
                    / 0{i + 1}
                  </span>
                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 border ${
                      p.img
                        ? "border-white/70 text-white"
                        : "border-white/60 text-white"
                    } group-hover:bg-[#F58220] group-hover:border-[#F58220] transition-colors`}
                  >
                    <ArrowUpRight size={16} />
                  </span>
                </div>

                <div>
                  <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight">
                    {p.title}
                  </h3>
                  <p
                    className={`mt-3 text-sm md:text-[15px] leading-relaxed font-medium max-w-md ${
                      p.img ? "text-white/85" : "text-white/85"
                    }`}
                  >
                    {p.desc}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
